import {Index} from "./index";
import {Article, ArticleSearchResult, Product, ProductSearchResult, SearchResult} from "../interfaces";
import * as lunr from "lunr";

import {logFactory} from "../log";
const log = logFactory('search:worker')

export class Search {
    simpleProductIndex: Index
    articleIndex: Index
    products: { [id: string]: Product }
    articles: { [id: string]: Article }
    readyCallback: ()=>void = this.defaultResults
    currentSearch:string = null
    noResultsResultsString = '[]';
    searchInitiated = false

    constructor(private worker: Worker) {
        worker.addEventListener("message", this.processMessage)
        Promise.all([
            Promise.all([
                Index.load('/search/product-index.json').then((i) => {
                    log('simple products loaded')
                    this.simpleProductIndex = i
                }),
                this.loadProducts()
                ]).then(() => {
                    log('All product data loaded')
                    if(this.readyCallback)
                        this.readyCallback()
                }),
            // this.loadArticles(),
            // Index.load('/search/article-index.json').then((i) => {
            //     log('articles loaded')
            //     this.articleIndex = i
            // }),
        ]).then(async () => {
            log('All data loaded')
            await this.prepNoResultsResults()
            if(this.readyCallback)
                this.readyCallback()
        })
    }

    search(term: string, forceSearch: boolean = false):void {
        log('search', term)
        this.searchInitiated = true
        if (term == '') {
            log('empty search... returning empty results')
            this.defaultResults()
            return
        }

        if  (!forceSearch && term === this.currentSearch) {
            log('search: term == currentSearch so ignoring request', term, this.currentSearch)
            return
        }

        if (this.products && term in this.products ){
            this.worker.postMessage({type: 'product-found', url: this.products[term].url})
        }

        this.currentSearch = term
        this.worker.postMessage({type: 'search-started', term})
        let ret: SearchResult[] = []
        if (this.simpleProductIndex) {
            ret = [...ret, ...this.transformProductResults(this.simpleProductIndex.search(term))]
        } else {
            this.readyCallback = () => this.search(term, true)
            return
        }

        if (this.articleIndex)
            ret = [...ret, ...this.transformArticleResults(this.articleIndex.search(term))]

        if (ret.length == 0)
            return this.worker.postMessage({type: 'noresults', results: this.noResultsResultsString })
        this.worker.postMessage({ type: 'search', results: JSON.stringify(ret) });
    }

    autoComplete(searchTerm: string): void {
        if (!searchTerm)
            return this.worker.postMessage({type: 'auto', results: '[]'});

        const term = searchTerm
        // onst [prepend, term] = this.splitSearchTerm(searchTerm)
        const index = this.simpleProductIndex.idx;
        const results = index.query(function (q) {
            // exact matches should have the highest boost
            q.term(searchTerm, {boost: 100})
            // wildcard matches should be boosted slightly
            q.term(searchTerm, {
                boost: 10,
                usePipeline: true,
                wildcard: lunr.Query.wildcard.LEADING | lunr.Query.wildcard.TRAILING
            })
            // finally, try a fuzzy search, without any boost
            q.term(searchTerm, {boost: 1, usePipeline: false, editDistance: 1})
        });
        if (!results.length) {
            return this.worker.postMessage({type: 'auto', results: '[]'});
        }
        const ret = results.map(function (v, i, a) { // extract unstemmed terms
            const unstemmedTerms: { [id: string]: boolean } = {};
            const metadata = <any>v.matchData.metadata
            Object.keys(metadata).forEach(function (term) {
                Object.keys(metadata[term]).forEach(function (field) {
                    metadata[term][field].unstemmed.forEach(function (word: string) {
                        unstemmedTerms[word] = true;
                    });
                });
            });
            return Object.keys(unstemmedTerms) //.map(i => `${prepend} ${term}`);
        }).reduce(function (a, b) { // flatten
            return a.concat(b);
        }).filter(function (v, i, a) { // uniq
            return a.indexOf(v) === i;
        });
        this.worker.postMessage({type: 'auto', results: JSON.stringify(ret)});
    }
    private async loadProducts() {
        const resp = await fetch('/search/products.json')
        if (resp.ok) {
            this.products = await resp.json()
            log('products loaded')
        }
    }
    private async loadArticles() {
        const resp = await fetch('/search/articles.json')
        if (resp.ok) {
            this.articles = await resp.json()
            log('articles loaded')
        }
    }
    private transformArticleResults(results: lunr.Index.Result[]) {
        const ret: ArticleSearchResult[] = []
        for (const r of results) {
            const slug = this.articleIndex.idMap[r.ref]
            if (slug in this.articles) {
                ret.push({
                    type: 'article',
                    article: Object.assign({}, this.articles[slug], {slug: slug})
                })
            }
        }
        return ret
    }
    private transformProductResults = (results: lunr.Index.Result[]): ProductSearchResult[] => {
        const ret: ProductSearchResult[] = []
        for (const r of results) {
            const sku = this.simpleProductIndex.idMap[r.ref]
            if (sku in this.products)
                ret.push({type: 'product', product: this.products[sku]})
        }
        return ret
    }
    private processMessage = (event: MessageEvent):void => {
        const term = event.data.term.toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, "")
        if (event.data.type == 'auto') {
            log(event.data, 'MESSAGE AUTOCOMPLETE')
            this.autoComplete(term)
        }
        if (event.data.type == 'search') {
            log(event.data, 'MESSAGE SEARCH')
            this.search(term)
        }
        if (event.data.type == 'default-results') {
            log(event.data, 'DEFAULT RESULTS')
            this.searchInitiated = true
            this.defaultResults()
        }
    }

    private sortByOrderDesc(b: {order:number}, a:{order:number}){
        if (a.order < b.order)
            return -1
        if (a.order > b.order)
            return 1
        return 0
    }
    private async prepNoResultsResults() {
        log('prepNoResultsResults')
        const products = Object.values(this.products).sort(this.sortByOrderDesc).slice(0, 14).map(i => {return {type: 'product', product: i}})
        //const articles = Object.values(this.articles).sort(this.sortByOrderDesc).slice(0, 26).map(i => {return {type: 'article', article: i}})
        this.noResultsResultsString = JSON.stringify([...products])
    }
    private defaultResults() {
        log('defaultresults')

        if (this.searchInitiated)
            this.worker.postMessage({type: 'noresults', results: this.noResultsResultsString})
    }
}